import {
  PanInfo,
  motion,
  useAnimationControls,
  useMotionValue,
  type Transition,
} from 'framer-motion'
import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'

import { useModalInstance, useModalTabsOptional } from '@context/ModalInstanceContext'
import { useModals } from '@context/ModalsContext'

import { MODAL_HEADER_GAP } from '@ui/components/Modal/Header/ModalHeader'
import {
  MODAL_EXIT_MS,
  useAnimatedModalClose,
} from '@ui/components/Modal/hooks/useAnimatedModalClose'
import { TAB_SIZE } from '@ui/components/Tabs/TabItem/TabItem'

import { useIsOnScreenKeyboardOpen } from '@hooks/useIsOnScreenKeyboardOpen'
import { useOnClickedOutside } from '@hooks/useOnClickedOutside'

import { DragProvider } from './DragContext'

import styles from './BottomSheet.module.scss'

const VELOCITY_THRESHOLD = 500
const SNAP_THRESHOLD = 70 / 100 // % of screen height

const openTransition: Transition = { type: 'spring', stiffness: 400, damping: 40 }

const closeTransition: Transition = {
  type: 'tween',
  duration: MODAL_EXIT_MS / 1000,
  ease: [0.2, 0.0, 0.2, 1],
}

export const BottomSheet: React.FC<PropsWithChildren> = ({ children }) => {
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)

  const controls = useAnimationControls()
  const { activeModal } = useModals()
  const { id, canClose, isClosing, contentRef, contentInnerRef } = useModalInstance()
  const tabsCtx = useModalTabsOptional()

  const dragHandleRef = useRef<HTMLDivElement>(null)
  const contentWrapperRef = useRef<HTMLDivElement>(null)

  const [mode, setMode] = useState<'fit' | 'snap'>('snap')
  const [fitHeight, setFitHeight] = useState(0)
  const [heightWithoutBody, setHeightWithoutBody] = useState(0)

  const snapThreshold = windowHeight * SNAP_THRESHOLD
  const fullHeight = windowHeight
  const minHeight = 0

  const height = useMotionValue(0)
  const isKeyboardOpen = useIsOnScreenKeyboardOpen(contentWrapperRef)

  const calculateAndSetFitHeight = useCallback(() => {
    if (isClosing) return

    const dragHandleHeight = dragHandleRef.current?.offsetHeight ?? 0
    const contentInnerHeight = contentInnerRef.current?.scrollHeight ?? 0

    const tabSize =
      tabsCtx?.size && tabsCtx?.activeId && TAB_SIZE[tabsCtx.size]
        ? TAB_SIZE[tabsCtx.size] + MODAL_HEADER_GAP
        : 0

    const calculatedFitHeight = dragHandleHeight + heightWithoutBody + contentInnerHeight + tabSize

    if (calculatedFitHeight < snapThreshold) {
      setMode('fit')
      setFitHeight(calculatedFitHeight)
    } else {
      setMode('snap')
      setFitHeight(snapThreshold)
    }
  }, [
    contentInnerRef,
    heightWithoutBody,
    isClosing,
    snapThreshold,
    tabsCtx?.activeId,
    tabsCtx?.size,
  ])

  useEffect(() => {
    const contentWrapperHeight = contentWrapperRef.current?.scrollHeight ?? 0
    const contentRefHeight = contentRef.current?.scrollHeight ?? 0

    if (contentRefHeight !== 0 && tabsCtx?.size && tabsCtx?.activeId) {
      setHeightWithoutBody(contentWrapperHeight + MODAL_HEADER_GAP)
    } else {
      setHeightWithoutBody(contentWrapperHeight)
    }

    const handleResize = () => setWindowHeight(window.innerHeight)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!contentInnerRef.current) return
    if (isClosing) return

    const resizeObserver = new ResizeObserver(calculateAndSetFitHeight)
    resizeObserver.observe(contentInnerRef.current)
    return () => resizeObserver.disconnect()
  }, [calculateAndSetFitHeight, contentInnerRef, isClosing])

  useEffect(() => {
    if (isClosing) return
    if (!isKeyboardOpen && fitHeight > 0) controls.start({ height: fitHeight }, openTransition)
  }, [controls, fitHeight, isClosing, isKeyboardOpen])

  useEffect(() => {
    if (isClosing) return
    if (isKeyboardOpen) controls.start({ height: fullHeight }, openTransition)
  }, [controls, fullHeight, isClosing, isKeyboardOpen])

  const playExit = useCallback(() => {
    controls.stop()
    return controls.start({ height: minHeight }, closeTransition)
  }, [controls, minHeight])

  const requestClose = useAnimatedModalClose(playExit)

  const isActive = activeModal?.id === id && !isClosing
  const bottomSheetRef = useOnClickedOutside<HTMLDivElement>(
    isActive && canClose ? requestClose : false
  )

  const onPan = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (isClosing) return
    if (isKeyboardOpen) return

    controls.stop()
    const currentHeight = height.get()
    const newHeight = currentHeight - info.delta.y

    if (mode !== 'fit' || newHeight <= fitHeight) {
      height.set(Math.max(minHeight, newHeight))
      return
    }

    const overdrag = newHeight - currentHeight
    const resistanceFactor = 0.75
    const resistedHeight = newHeight - overdrag * resistanceFactor
    height.set(resistedHeight)
  }

  const onPanEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (isClosing) return
    if (isKeyboardOpen) return

    const { velocity } = info
    const currentHeight = height.get()

    if (mode === 'fit') {
      const closeDragThreshold = fitHeight * 0.5
      if (currentHeight < closeDragThreshold && canClose) {
        requestClose()
        return
      }
      controls.start({ height: fitHeight }, openTransition)
      return
    }

    const closeDragThreshold = fullHeight * 0.5

    if (Math.abs(velocity.y) > VELOCITY_THRESHOLD) {
      if (velocity.y > 0) controls.start({ height: fitHeight }, openTransition)
      else controls.start({ height: fullHeight }, openTransition)
    }

    const snapPoints = [minHeight, fitHeight, fullHeight]
    const closestSnap = snapPoints.reduce((prev, curr) =>
      Math.abs(curr - currentHeight) < Math.abs(prev - currentHeight) ? curr : prev
    )

    if ((closestSnap === minHeight || currentHeight < closeDragThreshold) && canClose) {
      requestClose()
      return
    }

    controls.start({ height: closestSnap }, openTransition)
  }

  const handleTap = (event: MouseEvent | TouchEvent | PointerEvent) => {
    if (isClosing) return
    if (isKeyboardOpen) return

    if (
      mode === 'snap' &&
      event.target instanceof HTMLElement &&
      event.target.nodeName !== 'INPUT'
    ) {
      const currentHeight = height.get()
      if (Math.abs(currentHeight - fitHeight) < 5) {
        controls.start({ height: fullHeight }, openTransition)
      } else if (Math.abs(currentHeight - fullHeight) < 5) {
        controls.start({ height: fitHeight }, openTransition)
      }
    }
  }

  return (
    <motion.div
      className={styles.bottomSheet}
      style={{ height }}
      initial={{ height: 0 }}
      animate={controls}
      ref={bottomSheetRef}
    >
      <DragProvider value={{ onPan, onPanEnd, onTap: handleTap }}>
        <motion.div
          ref={dragHandleRef}
          className={styles.dragHandle}
          onPan={onPan}
          onPanEnd={onPanEnd}
          onTap={handleTap}
        >
          <div className={styles.dragHandleBar} />
        </motion.div>

        <div ref={contentWrapperRef} className={styles.content}>
          {children}
        </div>
      </DragProvider>
    </motion.div>
  )
}
